import React, { useEffect, useMemo, useState } from "react";
import SimplifiedSnapsWise from "./SnapsWise/SimplifiedSnapsWise";

function CompareSelector({
  label,
  value,
  options,
  onChange,
  color = "blue",
}) {
  const tone =
    color === "purple"
      ? {
          wrap: "from-purple-50 to-fuchsia-50 border-purple-200/60",
          badge: "from-purple-500 to-fuchsia-500",
          select:
            "border-purple-200 focus:ring-purple-300 focus:border-purple-300",
        }
      : {
          wrap: "from-blue-50 to-cyan-50 border-blue-200/60",
          badge: "from-blue-500 to-cyan-500",
          select: "border-blue-200 focus:ring-blue-300 focus:border-blue-300",
        };

  return (
    <div
      className={[
        "rounded-2xl border bg-gradient-to-r p-4 shadow-sm",
        tone.wrap,
      ].join(" ")}
    >
      <div className="mb-3 flex items-center gap-3">
        <div
          className={[
            "flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br text-sm text-white shadow-sm",
            tone.badge,
          ].join(" ")}
        >
          🎧
        </div>
        <div className="font-semibold text-slate-900">{label}</div>
      </div>

      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={[
          "w-full rounded-xl border bg-white px-3 py-3 text-sm text-slate-900 shadow-sm",
          "focus:outline-none focus:ring-2",
          tone.select,
        ].join(" ")}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function DialectInfoCard({
  title,
  subtitle,
  onClick,
  color = "blue",
}) {
  const tone =
    color === "purple"
      ? {
          wrap: "border-purple-200/70 bg-white/80 hover:bg-white",
          kicker: "text-purple-700",
          ring: "hover:border-purple-300",
        }
      : {
          wrap: "border-blue-200/70 bg-white/80 hover:bg-white",
          kicker: "text-blue-700",
          ring: "hover:border-blue-300",
        };

  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "w-full rounded-2xl border p-4 text-left shadow-sm transition-all",
        "hover:shadow-md",
        tone.wrap,
        tone.ring,
      ].join(" ")}
      title="Open dialect information"
    >
      <div
        className={[
          "text-[11px] font-bold uppercase tracking-[0.18em]",
          tone.kicker,
        ].join(" ")}
      >
        Dialect info
      </div>
      <div className="mt-2 truncate text-sm font-semibold text-slate-900">
        {title || "Dialect sample"}
      </div>
      <div className="mt-1 truncate text-xs text-slate-500">
        {subtitle || "Click to view details"}
      </div>
    </button>
  );
}

const basename = (p) => String(p || "").split("/").pop() || "";

const canonBase = (b) =>
  String(b || "")
    .replace(/\r/g, "")
    .replace(/\u00A0/g, " ")
    .trim()
    .replace(/dialogue/gi, "dialoge");

const normKey = (s) => canonBase(s).toLowerCase();

const getTranscriptFilename = (item) => {
  if (!item) return "";
  if (typeof item.file === "string") return item.file;
  if (item.file && typeof item.file === "object") {
    return item.file.file || item.file.full_path || "";
  }
  return item.full_path || item.audio_url || "";
};

const getOptionLabel = (metaItem, transcriptItem, index) => {
  return (
    metaItem?.uiTitle ||
    metaItem?.title ||
    transcriptItem?.uiTitle ||
    transcriptItem?.title ||
    basename(getTranscriptFilename(transcriptItem)) ||
    `Dialect ${index + 1}`
  );
};

export default function DialectCompareMode({
  subject,
  transcripts = [],
  course,
  context = [],
}) {
  const transcriptMap = useMemo(() => {
    const map = new Map();

    for (const t of transcripts) {
      const raw = basename(getTranscriptFilename(t));
      if (!raw) continue;

      map.set(raw, t);
      map.set(canonBase(raw), t);
      map.set(normKey(raw), t);
    }

    return map;
  }, [transcripts]);

  const compareOptions = useMemo(() => {
    const meta = Array.isArray(course?.audioMeta) ? course.audioMeta : [];

    if (meta.length > 0) {
      return meta
        .map((m, index) => {
          const raw = basename(m?.file);
          const linkedTranscript =
            transcriptMap.get(raw) ||
            transcriptMap.get(canonBase(raw)) ||
            transcriptMap.get(normKey(raw)) ||
            null;

          return {
            value: raw,
            label: getOptionLabel(m, linkedTranscript, index),
            meta: m,
            transcript: linkedTranscript,
          };
        })
        .filter((x) => x.value && x.transcript);
    }

    return transcripts
      .map((t, index) => {
        const raw = basename(getTranscriptFilename(t));
        return {
          value: raw,
          label: getOptionLabel(null, t, index),
          meta: null,
          transcript: t,
        };
      })
      .filter((x) => x.value && x.transcript);
  }, [course?.audioMeta, transcriptMap, transcripts]);

  const [leftFile, setLeftFile] = useState("");
  const [rightFile, setRightFile] = useState("");
  const [leftInfoOpen, setLeftInfoOpen] = useState(false);
  const [rightInfoOpen, setRightInfoOpen] = useState(false);

  useEffect(() => {
    if (!compareOptions.length) return;

    setLeftFile((prev) =>
      prev && compareOptions.some((x) => x.value === prev)
        ? prev
        : compareOptions[0]?.value || ""
    );

    setRightFile((prev) =>
      prev && compareOptions.some((x) => x.value === prev)
        ? prev
        : compareOptions[1]?.value || compareOptions[0]?.value || ""
    );
  }, [compareOptions]);

  useEffect(() => {
    setLeftInfoOpen(false);
  }, [leftFile]);

  useEffect(() => {
    setRightInfoOpen(false);
  }, [rightFile]);

  const leftOption = useMemo(
    () => compareOptions.find((x) => x.value === leftFile) || null,
    [compareOptions, leftFile]
  );

  const rightOption = useMemo(
    () => compareOptions.find((x) => x.value === rightFile) || null,
    [compareOptions, rightFile]
  );

  const leftScript = leftOption?.transcript || null;
  const rightScript = rightOption?.transcript || null;

  const findContextForFile = (script) => {
    const filename = basename(getTranscriptFilename(script));
    if (!filename || !Array.isArray(context)) return null;

    return (
      context.find((item) => {
        const audioFilename = basename(item?.audio_filename);
        const localPath = basename(item?.local_audio_path);
        const audioUrl = basename(item?.audio_url);

        return (
          audioFilename === filename ||
          localPath === filename ||
          audioUrl === filename
        );
      }) || null
    );
  };

  const leftContext = useMemo(
    () => findContextForFile(leftScript),
    [leftScript, context]
  );

  const rightContext = useMemo(
    () => findContextForFile(rightScript),
    [rightScript, context]
  );

  const leftInfoTitle =
    leftContext?.place || leftOption?.label || "Dialect A";

  const rightInfoTitle =
    rightContext?.place || rightOption?.label || "Dialect B";

  const leftInfoSubtitle = [leftContext?.kommune, leftContext?.fylke]
    .filter(Boolean)
    .join(" • ");

  const rightInfoSubtitle = [rightContext?.kommune, rightContext?.fylke]
    .filter(Boolean)
    .join(" • ");

  if (!compareOptions.length) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="rounded-2xl border border-white/40 bg-white/70 px-6 py-5 text-slate-700 shadow-lg">
          No dialect files available for compare mode.
        </div>
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 min-w-0 overflow-hidden">
      <div className="h-full min-h-0 min-w-0 overflow-y-auto overflow-x-hidden p-4 pb-6 xl:p-6">
        <div className="grid gap-6 2xl:grid-cols-2">
          <div className="flex flex-col gap-5">
            <div className="grid gap-4 xl:grid-cols-[minmax(0,1.4fr)_minmax(260px,1fr)]">
              <CompareSelector
                label="Dialect A"
                value={leftFile}
                options={compareOptions}
                onChange={setLeftFile}
                color="blue"
              />

              <DialectInfoCard
                title={leftInfoTitle}
                subtitle={leftInfoSubtitle}
                onClick={() => setLeftInfoOpen(true)}
                color="blue"
              />
            </div>

            <SimplifiedSnapsWise
              key={`left-${leftFile}`}
              subject={subject}
              file={leftScript}
              sectionTitle={leftInfoTitle}
              accent="blue"
              contextItem={leftContext}
              showContextHelp={leftInfoOpen}
              onCloseContextHelp={() => setLeftInfoOpen(false)}
            />
          </div>

          <div className="flex flex-col gap-5">
            <div className="grid gap-4 xl:grid-cols-[minmax(0,1.4fr)_minmax(260px,1fr)]">
              <CompareSelector
                label="Dialect B"
                value={rightFile}
                options={compareOptions}
                onChange={setRightFile}
                color="purple"
              />

              <DialectInfoCard
                title={rightInfoTitle}
                subtitle={rightInfoSubtitle}
                onClick={() => setRightInfoOpen(true)}
                color="purple"
              />
            </div>

            <SimplifiedSnapsWise
              key={`right-${rightFile}`}
              subject={subject}
              file={rightScript}
              sectionTitle={rightInfoTitle}
              accent="purple"
              contextItem={rightContext}
              showContextHelp={rightInfoOpen}
              onCloseContextHelp={() => setRightInfoOpen(false)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}